import React, { useState, useCallback } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import { designTokens } from '@/utils/design-tokens'
import { TaskService } from '@/api'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'

interface BulkDepartmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const BulkDepartmentModal: React.FC<BulkDepartmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showSuccess, showError } = useAlert()

  const [fromDepartmentId, setFromDepartmentId] = useState('')
  const [fromDepartmentName, setFromDepartmentName] = useState('')
  const [toDepartmentId, setToDepartmentId] = useState('')
  const [toDepartmentName, setToDepartmentName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = useCallback(() => {
    setFromDepartmentId('')
    setFromDepartmentName('')
    setToDepartmentId('')
    setToDepartmentName('')
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  const isFormValid = fromDepartmentId && toDepartmentId && fromDepartmentId !== toDepartmentId

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || !selectedWorkspace?.id) return

    setIsSubmitting(true)

    try {
      const response = await TaskService.appsTaskViewsBulkReplaceDepartment({
        requestBody: {
          workspace_id: selectedWorkspace.id,
          from_department_id: fromDepartmentId,
          to_department_id: toDepartmentId,
        },
      })

      showSuccess(`부서 일괄 수정 완료 (${response.updated_count}건 변경)`)
      handleClose()
      onSuccess()
    } catch (err) {
      console.error('부서 일괄 수정 실패:', err)
      showError('부서 일괄 수정에 실패했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }, [isFormValid, selectedWorkspace, fromDepartmentId, toDepartmentId, handleClose, onSuccess, showSuccess, showError])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-[520px] mx-4"
        style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderBottomColor: designTokens.colors.gray[1] }}
        >
          <h2
            style={{
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: 700,
              color: designTokens.colors.gray[6],
            }}
          >
            부서 일괄 수정
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* 기존 부서 */}
          <div>
            <label
              className="block mb-2"
              style={{
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6],
              }}
            >
              기존 부서
            </label>
            <DepartmentSelect
              workspaceId={selectedWorkspace?.id || ''}
              selectedDepartmentId={fromDepartmentId}
              selectedDepartmentName={fromDepartmentName || '기존 부서 선택'}
              onSelect={(id: string, name: string) => {
                setFromDepartmentId(id)
                setFromDepartmentName(name)
              }}
              placeholder="기존 부서 선택"
            />
          </div>

          {/* 새 부서 */}
          <div>
            <label
              className="block mb-2"
              style={{
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6],
              }}
            >
              새 부서
            </label>
            <DepartmentSelect
              workspaceId={selectedWorkspace?.id || ''}
              selectedDepartmentId={toDepartmentId}
              selectedDepartmentName={toDepartmentName || '새 부서 선택'}
              onSelect={(id: string, name: string) => {
                setToDepartmentId(id)
                setToDepartmentName(name)
              }}
              placeholder="새 부서 선택"
            />
          </div>

          {/* 동일 부서 경고 */}
          {fromDepartmentId && toDepartmentId && fromDepartmentId === toDepartmentId && (
            <div
              className="p-3 rounded"
              style={{
                backgroundColor: '#FEF9C3',
                fontSize: designTokens.typography.fontSize['body-small'][0],
                color: designTokens.colors.gray[6],
              }}
            >
              기존 부서와 새 부서가 동일합니다.
            </div>
          )}

          {/* 안내 문구 */}
          <div
            className="p-3 rounded"
            style={{
              backgroundColor: designTokens.colors.gray[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[5],
              lineHeight: '1.5',
            }}
          >
            기존 부서가 배정된 모든 세부실행과제의 부서를 새 부서로 일괄 교체합니다.
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4 border-t"
          style={{ borderTopColor: designTokens.colors.gray[1] }}
        >
          <Button
            size="medium"
            color="white"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            취소
          </Button>
          <Button
            size="medium"
            color="dark"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? '처리 중...' : '일괄 수정'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BulkDepartmentModal
